// Other-agents presence store. Populated live from server-broadcast
// `agent:presence` events (see bus-bridge.ts) so the active agent can see who
// else is online, their role, and avatar.

import { create } from 'zustand';
import type { AgentAvailability, AgentId } from '@prakash-software/shared';

export interface OtherAgent {
  agentId: AgentId;
  name: string;
  initials: string;
  availability: AgentAvailability;
  /** Site role — drives the admin ring in the presence roster. */
  role?: 'agent' | 'site-admin' | 'account-admin' | 'super-admin';
  /** Avatar photo; the roster falls back to initials when absent. */
  avatarUrl?: string;
  /**
   * Every site this agent is staffing right now.
   *
   * A SET, not a field, because `agent:presence` is broadcast per site room:
   * one agent on three sites arrives as three events. Overwriting the row on
   * each of them — which is what the store used to do — kept only whichever
   * landed last, so the roster could not answer "who is covering this site",
   * which is the actual question when several sites share a team.
   */
  siteIds: Set<string>;
  chatsInUse: number;
  chatCap: number;
  onVideo: boolean;
}

interface PresenceState {
  others: Map<AgentId, OtherAgent>;
  /** Record this agent online on one site, merging with any sites already known. */
  upsert: (a: OtherAgent) => void;
  patch: (id: AgentId, p: Partial<OtherAgent>) => void;
  /** Drop the agent outright, whatever sites they were on. */
  remove: (id: AgentId) => void;
  /**
   * Record this agent offline on ONE site. They stay in the roster while they
   * still staff another — a per-site offline is not "logged out", and treating
   * it as such made an agent vanish from every site the moment they closed one.
   */
  removeFromSite: (id: AgentId, siteId: string) => void;
}

export const usePresenceStore = create<PresenceState>((set) => ({
  others: new Map(),
  upsert: (a) =>
    set((s) => {
      const existing = s.others.get(a.agentId);
      const next = new Map(s.others);
      // Union the sites: this event names one room, not the whole set.
      const siteIds = new Set(existing?.siteIds ?? []);
      for (const id of a.siteIds) siteIds.add(id);
      next.set(a.agentId, { ...existing, ...a, siteIds });
      return { others: next };
    }),
  patch: (id, p) =>
    set((s) => {
      const existing = s.others.get(id);
      if (!existing) return s;
      const next = new Map(s.others);
      next.set(id, { ...existing, ...p });
      return { others: next };
    }),
  remove: (id) =>
    set((s) => {
      const next = new Map(s.others);
      next.delete(id);
      return { others: next };
    }),
  removeFromSite: (id, siteId) =>
    set((s) => {
      const existing = s.others.get(id);
      if (!existing) return s;
      const siteIds = new Set(existing.siteIds);
      siteIds.delete(siteId);
      const next = new Map(s.others);
      if (siteIds.size === 0) next.delete(id);
      else next.set(id, { ...existing, siteIds });
      return { others: next };
    }),
}));

export const selectOthers = (s: PresenceState): OtherAgent[] => Array.from(s.others.values());
